/**
 * Admin → Edit locations (1.0.0-rc.6): the folders editors' workstations open, where Send to edit puts timelines and
 * media for Premiere, Resolve, Final Cut and Avid. A server folder under PRISM_EDIT_ROOT (storage of its own, never
 * mixed with PRISM's media), a folder under a storage agent's edit root, for a regional office, or (1.0.0-rc.9) a bucket
 * that a NAS also serves over SMB. Each has named workstation mappings.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clapperboard, HelpCircle } from 'lucide-react';
import { useStorage } from '../../api/hooks';
import { AddLocationDialog } from './storage/AddLocationDialog';
import { LocationCard } from './storage/LocationCard';

export function EditLocationsAdmin() {
  const q = useStorage();
  const items = q.data?.items ?? [];
  const edit = items.filter((l) => l.purpose === 'edit');
  const [adding, setAdding] = useState(false);
  return (
    <div className="page" data-testid="edit-locations-admin">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <h2 style={{ margin: 0 }}>Edit locations</h2>
        <span style={{ flex: 1 }} />
        <Link to="/help/nle#admin" className="btn ghost sm" data-testid="edit-locations-help"><HelpCircle size={13} /> Help</Link>
        <button className="btn" onClick={() => setAdding(true)} data-testid="add-edit-location"><Clapperboard size={14} /> Add edit location</button>
      </div>
      <p className="lede">
        Folders editors open in their NLE. Send to edit writes a folder here for each hand-off: the media, the timelines (FCP7 XML for
        Premiere, FCPXML for Final Cut and Resolve, OTIO, AAF for Avid, an EDL), graphics rendered with alpha, subtitles and a manifest.
        Add a workstation mapping for each way editors open it (a Windows share, a Mac volume, another office’s mount), so the
        timelines link without relinking. A server edit location lives under its own root, PRISM_EDIT_ROOT, apart from PRISM’s
        storage; a folder on a storage agent serves the editors at its site; a bucket works when a NAS serves the same data over S3 and SMB.
      </p>
      {q.isLoading ? <div className="skeleton" style={{ height: 160 }} /> : (
        <div className="cards" data-testid="edit-locations">
          {edit.map((l) => <LocationCard key={l.id} loc={l} all={items} />)}
          {edit.length === 0 && <p className="muted">No edit locations yet. Add one to let people send sequences and clips to an NLE.</p>}
        </div>
      )}
      {adding && <AddLocationDialog purpose="edit" onClose={() => setAdding(false)} />}
    </div>
  );
}
